/**
 * Phase 23: useSubscription hook
 *
 * Phase 23.2: added `noSubscription` and fixed `isLoading`.
 *
 * The store now exposes `subscriptionLoaded` — true once the subscription
 * fetch has completed for the active workspace, regardless of whether a row
 * came back. This lets the hook distinguish three states:
 *
 *   - isLoading        : fetch still in flight
 *   - noSubscription   : fetch done, workspace has no subscription row
 *                        (a workspace created after the billing migration —
 *                         it should be shown pricing cards so it can subscribe)
 *   - (otherwise)      : fetch done, a subscription row exists
 *
 * Before this fix, `isLoading` was `subscription === null`, which could never
 * become false for a workspace with no row — the Billing page spun forever.
 */

import { useProjectStore } from '@/store/useProjectStore';
import {
  type Tier,
  type TierDefinition,
  type TierLimits,
  hasActiveAccess,
  isTrialing,
  isGrandfathered,
  trialKind,
  trialDaysRemaining,
  tierFor,
  checkLimit,
  type LimitCheckResult,
} from '@/lib/billing/tiers';
import type { Subscription } from '@/lib/billing/api';

export interface SubscriptionInfo {
  subscription: Subscription | null;
  tier: TierDefinition;
  tierKey: Tier;
  /** Fetch still in flight. */
  isLoading: boolean;
  /** Fetch complete, and this workspace has no subscription row. */
  noSubscription: boolean;
  hasAccess: boolean;
  isTrialing: boolean;
  isGrandfathered: boolean;
  /** Phase 24: which kind of trial — 'grandfathered' | 'signup' | 'paid' | 'none'. Display-only. */
  trialKind: ReturnType<typeof trialKind>;
  trialDaysLeft: number;
  seatCount: number;
  checkLimit: (metric: keyof TierLimits, nextCount: number) => LimitCheckResult;
  reload: () => Promise<void>;
}

export function useSubscription(): SubscriptionInfo {
  const subscription = useProjectStore((s) => s.subscription);
  const subscriptionLoaded = useProjectStore((s) => s.subscriptionLoaded);
  const mode = useProjectStore((s) => s.mode);
  const isInitialized = useProjectStore((s) => s.isInitialized);
  const reload = useProjectStore((s) => s.reloadSubscription);

  // Loading only while in API mode, initialized, and the fetch hasn't
  // completed yet. Once subscriptionLoaded is true, we are NOT loading —
  // even if subscription is still null (that just means "no row").
  const isLoading = mode === 'api' && isInitialized && !subscriptionLoaded;

  // Fetch finished, but there's no subscription row for this workspace.
  const noSubscription = subscriptionLoaded && subscription === null;

  const tier = tierFor(subscription);

  return {
    subscription,
    tier,
    tierKey: tier.key,
    isLoading,
    noSubscription,
    hasAccess: hasActiveAccess(subscription),
    isTrialing: isTrialing(subscription),
    isGrandfathered: isGrandfathered(subscription),
    trialKind: trialKind(subscription),
    trialDaysLeft: trialDaysRemaining(subscription),
    seatCount: subscription?.quantity ?? 0,
    checkLimit: (metric, nextCount) => checkLimit(subscription, metric, nextCount),
    reload,
  };
}
